import React from 'react';
import { classNames } from 'primereact/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApiFetch } from '../../api/fetcher';
import { useAuth } from '../../auth/AuthContext';
import { LOGOUT_URL } from '../../api/urls';
import './Sidebar.scss';

interface SidebarProps {
    visible?: boolean;
    onHide?: () => void;
}

const navItems = [
    { id: 'sidebar_menu_1', label: 'Strona główna', iconSrc: '/sidebar/sidebar-home.svg', path: '/' },
    { id: 'sidebar_menu_2', label: 'Flota', iconSrc: '/sidebar/sidebar-fleet.svg', path: '/fleet/' },
    { id: 'sidebar_menu_3', label: 'Twoja oferta', iconSrc: '/sidebar/sidebar-offer.svg', path: '/offer/' },
    { id: 'sidebar_menu_4', label: 'Historia usług', iconSrc: '/sidebar/sidebar-history.svg', path: '/orders/' },
    { id: 'sidebar_menu_5', label: 'Transakcje', iconSrc: '/sidebar/sidebar-transactions.svg', path: '/transactions/' },
    { id: 'sidebar_menu_6', label: 'Dokumenty', iconSrc: '/sidebar/sidebar-documents.svg', path: '/documents/' },
    { id: 'sidebar_menu_7', label: 'Kontakt', iconSrc: '/sidebar/sidebar-contact.svg', path: '/contact/' },
];

const Sidebar: React.FC<SidebarProps> = ({ visible = true, onHide }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth() as any;
    const apiFetch = useApiFetch();
    const sidebarRef = React.useRef<HTMLDivElement>(null);

    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    React.useEffect(() => {
        if (!isMobile || !visible) return;

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
                if (onHide) onHide();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isMobile, visible, onHide]);

    const handleNavClick = (path: string) => {
        navigate(path);
        if (onHide) onHide();
    };

    const handleSettingsClick = () => {
        if (location.pathname !== '/settings/') {
            navigate('/settings/', { state: { from: location.pathname } });
        }
        if (onHide) onHide();
    };

    const handleLogout = async () => {
        try {
            await apiFetch(LOGOUT_URL, { method: 'POST', credentials: 'include' });
        } catch { }
        logout();
        navigate('/login/', { replace: true });
    };

    return (
        <div
            ref={sidebarRef}
            className={classNames('sidebar', 'pp-font-geist', { 'sidebar-mobile': isMobile, 'sidebar-hidden': isMobile && !visible })}
        >
            <div className="sidebar__logo-bar">
                <img
                    alt="mooveno-logo"
                    className="sidebar__logo"
                    src="/sidebar/mooveno-logo.svg"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleNavClick('/')}
                />
            </div>
            <div className="sidebar__body">
                <nav className="sidebar__nav">
                    {navItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <div
                                key={item.id}
                                className={classNames('sidebar-nav-item', { 'sidebar-nav-item--active': isActive })}
                                onClick={() => handleNavClick(item.path)}
                            >
                                <span
                                    className="sidebar-nav-item__icon"
                                    style={{ WebkitMaskImage: `url(${item.iconSrc})`, maskImage: `url(${item.iconSrc})` }}
                                />
                                <span className="sidebar-nav-item__label">{item.label}</span>
                            </div>
                        );
                    })}
                </nav>
                <div className="sidebar__footer">
                    <button type="button" className="sidebar__footer-button" onClick={handleSettingsClick}>
                        <span
                            className="sidebar__footer-button-icon"
                            style={{ WebkitMaskImage: `url(/sidebar/sidebar-settings.svg)`, maskImage: `url(/sidebar/sidebar-settings.svg)` }}
                        />
                        <span>Ustawienia</span>
                    </button>
                    <button type="button" className="sidebar__footer-button" onClick={handleLogout}>
                        <span
                            className="sidebar__footer-button-icon"
                            style={{ WebkitMaskImage: `url(/sidebar/sidebar-logout.svg)`, maskImage: `url(/sidebar/sidebar-logout.svg)` }}
                        />
                        <span>Wyloguj</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
